import { Route, Routes, Navigate } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import SystemLogPage from '../pages/admin/SystemLogPage/index';
import SystemPage from '../pages/admin/SystemPage/index';
import PresenceManagementPage from '../pages/admin/PresenceManagement/index';
import InfrastructureMonitoring from '../pages/admin/InfrastructureMonitoring/index';
import UserListPage from '../pages/admin/Users/index';
import ViewUserPage from '../pages/admin/Users/ViewUser';
import DataDeletionRequestsPage from '../pages/admin/DataDeletionRequests/index';
import PermissionsPage from '../pages/admin/Permissions/index';
import AssignRightsPage from '../pages/admin/Permissions/AssignRights';
import CommunicationsRoutes from './CommunicationsRoutes';
import FileManagementPage from '../pages/admin/FileManagement/index';
import AiManagementPage from '../pages/admin/AiManagement/index';
import ConnectorAdminPage from '../pages/admin/Connector/index';
import ApiKeysAdminPage from '../pages/admin/ApiKeys/index';
import AiFeatureFlagsPage from '../pages/admin/AiManagement/FeatureFlags/index';
import AiPresetsPage from '../pages/admin/AiManagement/Presets/index';
import AiUsagePage from '../pages/admin/AiManagement/Usage/index';
import AppCatalogueRoutes from './AppCatalogueRoutes';

/**
 * Admin Routes
 * Handles all /admin/* routes for system administration
 *
 * Pages:
 * - /admin/system-log - Real-time system logs
 * - /admin/system - System health and configuration
 * - /admin/presence - Presence management and analytics
 * - /admin/infrastructure - RabbitMQ and Redis infrastructure monitoring
 * - /admin/users - User management (list, create, edit, delete)
 * - /admin/users/:id - View user details with tabs
 * - /admin/permissions - Permissions management
 * - /admin/app-catalogue/* - Constellation App Catalogue domain app (Overview, Apps, Recommendations, Entitlements, Features)
 * - /admin/communications - Communications management (Email, SMS, WhatsApp, Push)
 * - /admin/communications/email - Email queue, templates, and testing
 * - /admin/communications/email/view/:id - View email details with HTML body
 * - /admin/communications/sms - SMS queue, templates, and testing
 * - /admin/communications/whatsapp - WhatsApp queue, templates, parameters, media, and testing
 * - /admin/communications/push - Push notification queue, templates, and testing
 * - /admin/file-management - File and folder management with AWS S3
 * - /admin/ai - AI Management dashboard
 * - /admin/ai/feature-flags - AI feature flag management (enable/disable per tenant)
 * - /admin/ai/presets - AI workflow preset management (system + custom)
 * - /admin/ai/usage - AI usage logs and analytics
 * - /admin/reports - System reports (Coming Soon)
 */
export default function AdminRoutes() {
  return (
    <Routes>
      <Route path="system-log" element={
        <MainLayout>
          <SystemLogPage /> 
        </MainLayout> 
      } />

      <Route path="system" element={
        <MainLayout>
          <SystemPage />
        </MainLayout>
      } />

      <Route path="presence" element={
        <MainLayout>
          <PresenceManagementPage />
        </MainLayout>
      } />

      <Route path="infrastructure" element={
        <MainLayout>
          <InfrastructureMonitoring />
        </MainLayout>
      } />

      <Route path="users" element={
        <MainLayout>
          <UserListPage />
        </MainLayout>
      } />

      <Route path="users/:id" element={
        <MainLayout>
          <ViewUserPage />
        </MainLayout>
      } />

      <Route path="data-deletion-requests" element={
        <MainLayout>
          <DataDeletionRequestsPage />
        </MainLayout>
      } />

      <Route path="data-deletion-requests/:id" element={
        <MainLayout>
          <DataDeletionRequestsPage />
        </MainLayout>
      } />

      <Route path="permissions" element={
        <MainLayout>
          <PermissionsPage />
        </MainLayout>
      } />

      <Route path="permissions/roles/:id/assign" element={
        <MainLayout>
          <AssignRightsPage />
        </MainLayout>
      } />

      {/* App Catalogue — full domain app with its own left sidebar.
          Mounted as a nested splat so all /admin/app-catalogue/* pages are
          handled by AppCatalogueRoutes, which wraps each page in MainLayout
          with domainKey="app-catalogue". Stays under the Admin gate (this
          AdminRoutes tree is mounted inside the Admin ProtectedRoute in App.tsx). */}
      <Route path="app-catalogue/*" element={<AppCatalogueRoutes />} />

      {/* Communications — hub + per-channel domain-app shells with their own
          sidebars. Mounted as a nested splat; CommunicationsRoutes wraps each
          channel page in MainLayout with the channel's domainKey. */}
      <Route path="communications/*" element={<CommunicationsRoutes />} />

      {/* LLM (MCP) connector: switches, sessions, audit log, clients. Connector.Admin right. */}
      <Route path="connector" element={
        <MainLayout>
          <ConnectorAdminPage />
        </MainLayout>
      } />

      {/* Console-issued API keys for external integrations. ApiKeys.View / ApiKeys.Manage. */}
      <Route path="api-keys" element={
        <MainLayout>
          <ApiKeysAdminPage />
        </MainLayout>
      } />

      <Route path="file-management" element={
        <MainLayout>
          <FileManagementPage />
        </MainLayout>
      } />

      <Route path="ai" element={
        <MainLayout domainKey="ai">
          <AiManagementPage />
        </MainLayout>
      } />

      <Route path="ai/feature-flags" element={
        <MainLayout domainKey="ai">
          <AiFeatureFlagsPage />
        </MainLayout>
      } />

      <Route path="ai/presets" element={
        <MainLayout domainKey="ai">
          <AiPresetsPage />
        </MainLayout>
      } />

      <Route path="ai/usage" element={
        <MainLayout domainKey="ai">
          <AiUsagePage />
        </MainLayout>
      } />

      <Route path="reports" element={
        <MainLayout>
          <div>Reports Page - Coming Soon</div>
        </MainLayout>
      } />

      {/* Redirect /admin to home */}
      <Route path="/" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
